import { select } from "redux-saga/effects";

import { WidgetTypes } from "legacy/constants/WidgetConstants";
import type {
  WidgetCardProps,
  WidgetType,
} from "legacy/constants/WidgetConstants";
import { generateReactKey } from "legacy/utils/generators";
import { getApplicationSettings } from "store/slices/application/selectors";
import { Flag, selectFlagById } from "store/slices/featureFlags";
import { createSaga } from "store/utils/saga";
import { ROOT } from "store/utils/types";

import { baseWidgets } from "../baseWidgets";
import type { WidgetCardsCategorized } from "../baseWidgets";
import slice from "../slice";

interface GetAvailableWidgetsArgs {
  applicationId: string;
}

function* getAvailableWidgetsInternal({
  applicationId,
}: GetAvailableWidgetsArgs) {
  const appSettings: ReturnType<typeof getApplicationSettings> = yield (
    select(getApplicationSettings) ?? {}
  );
  const customWidgets: WidgetCardProps[] = Object.values(
    appSettings?.registeredComponents ?? {},
  ).map((value: any) => ({
    type: `${WidgetTypes.CUSTOM_WIDGET}:${value.id}` as WidgetType,
    widgetCardName: value.displayName,
    key: generateReactKey(),
  }));

  const isMenuWidgetEnabled: boolean = yield select(
    selectFlagById,
    Flag.ENABLE_MENU_WIDGET,
  );

  const isKeyValueWidgetEnabled: boolean = yield select(
    selectFlagById,
    Flag.ENABLE_KEY_VALUE_WIDGET,
  );

  const optionalCommonWidgets: [WidgetType, boolean][] = [
    [WidgetTypes.MENU_WIDGET, isMenuWidgetEnabled],
    [WidgetTypes.KEY_VALUE_WIDGET, isKeyValueWidgetEnabled],
  ];
  const commonWidgetsToExclude = optionalCommonWidgets.reduce(
    (acc: WidgetType[], [type, isEnabled]) =>
      !isEnabled ? [...acc, type] : acc,
    [],
  );

  const widgets: WidgetCardsCategorized = baseWidgets.map(
    (categorizedWidgetCards) => {
      if (categorizedWidgetCards.widgetCategory === "CUSTOM") {
        return {
          ...categorizedWidgetCards,
          widgetCards: customWidgets,
        };
      }

      if (
        commonWidgetsToExclude.length > 0 &&
        categorizedWidgetCards.widgetCategory === "COMMON"
      ) {
        return {
          ...categorizedWidgetCards,
          widgetCards: categorizedWidgetCards.widgetCards.filter(
            (card) => !commonWidgetsToExclude.includes(card.type),
          ),
        };
      }

      return categorizedWidgetCards;
    },
  );

  return { widgets, applicationId };
}

export const getAvailableWidgetsSaga = createSaga(
  getAvailableWidgetsInternal,
  "getAvailableWidgets",
  {
    sliceName: "availableWidgets",
  },
);

slice.saga(getAvailableWidgetsSaga, {
  start(state) {
    state.loading[ROOT] = true;
    delete state.errors[ROOT];
  },
  success(state, { payload: { widgets } }) {
    state.entities[ROOT] = { widgets };
    delete state.loading[ROOT];
  },
  error(state, { payload }) {
    state.errors[ROOT] = { error: payload };
    delete state.loading[ROOT];
  },
});
